import type { ComponentType } from 'react';
import type { useNavigate } from '@tanstack/react-router';
import type { BillingTab } from '@/features/billing/search-schema';
import { SETTINGS_SECTIONS } from '@/features/settings/search-schema';
import {
  SECTION_ACCESS,
  SECTION_ICON,
  SECTION_META,
  type SectionScope,
} from '@/features/settings/section-meta';
import * as Icon from '@/icons/Icon';
import { ACCESS, type Access } from '@/features/access/access';

/**
 * Single static registry of every page the command palette can jump to.
 *
 * Scope: the app's actual navigable surfaces — the sidebar's nav groups
 * (`Sidebar.tsx`'s `NAV_ITEMS`/`ORG_NAV_ITEMS`), `BillingTabNav.tsx`'s three
 * tabs, and `SettingsRail.tsx`'s nine sections. Deliberately excludes: auth
 * routes, OAuth/DWD callback routes, the `/email-accounts/:id/*` detail
 * routes (need a live id, not a static destination), and `/dashboard` (not
 * linked from the sidebar or anywhere else in the app today).
 *
 * The settings-section half of this list is generated FROM
 * `SECTION_META`/`SECTION_ACCESS`/`SECTION_ICON`
 * rather than re-typing each title/gate/icon, so a settings section renamed,
 * re-gated, or re-iconed in `section-meta.ts` can't drift out of sync with
 * the palette (or with `SettingsRail.tsx`, which reads the same
 * `SECTION_ICON`). The sidebar's
 * own `NAV_ITEMS`/`ORG_NAV_ITEMS` and `BillingTabNav`'s tab labels are NOT
 * similarly hoisted — they're tightly coupled to their component's
 * icons/JSX and re-typing three plain strings here is cheaper than forcing
 * that component to export plain data just for this file to import.
 */

export type CommandNavigate = ReturnType<typeof useNavigate>;

export type CommandPageItem = {
  id: string;
  label: string;
  /** Rendered as the row's secondary line, e.g. "Organization › API Keys". */
  breadcrumb: string;
  /** The route path, matched as a search keyword (e.g. typing "/billing"). */
  route: string;
  /** Same glyph the item's real source UI renders: `Sidebar.tsx`'s
   * `NAV_ITEMS`/`ORG_NAV_ITEMS` for the top-level surfaces (so every Billing
   * tab shares `CreditCard` — `BillingTabNav.tsx` renders no icon of its own
   * to match against), `section-meta.ts`'s `SECTION_ICON` for the 9 Settings
   * sections (the same source `SettingsRail.tsx` reads, so the two can't
   * drift). */
  icon: ComponentType<{ size?: number }>;
  /** Extra terms the query should match beyond `label`/`breadcrumb`/`route`. */
  keywords?: string[];
  /** Role-map entry the destination needs; a role-blocked page is omitted, like the sidebar's. */
  access?: Access;
  go: (navigate: CommandNavigate) => void;
};

const SCOPE_GROUP_LABEL: Record<SectionScope, string> = {
  account: 'Account',
  workspace: 'Workspace',
  org: 'Organization',
};

const SETTINGS_PAGES: CommandPageItem[] = SETTINGS_SECTIONS.map((section) => {
  const meta = SECTION_META[section];
  return {
    id: `settings-${section}`,
    label: meta.title,
    breadcrumb: `${SCOPE_GROUP_LABEL[meta.scope]} › Settings › ${meta.title}`,
    route: `/settings?section=${section}`,
    // Read from the same `section-meta.ts` map `SettingsRail.tsx` reads —
    // see the `icon` field's doc comment on `CommandPageItem` above.
    icon: SECTION_ICON[section],
    access: SECTION_ACCESS[section],
    go: (navigate) => void navigate({ to: '/settings', search: { section } }),
  };
});

const BILLING_TAB_META: Array<{ tab: BillingTab; label: string; access?: Access }> = [
  { tab: 'overview', label: 'Overview' },
  // `Plans` needs billing writes (OWNER) — same gate as `BillingTabNav`'s `manageOnly` flag.
  { tab: 'plans', label: 'Plans', access: ACCESS.billingManage },
  { tab: 'invoices', label: 'Invoices & history' },
];

const BILLING_PAGES: CommandPageItem[] = BILLING_TAB_META.map(({ tab, label, access }) => ({
  id: `billing-${tab}`,
  label,
  breadcrumb: `Account › Billing › ${label}`,
  route: `/billing?tab=${tab}`,
  // Same single glyph the sidebar's one "Billing" nav entry renders — all
  // three tabs share it.
  icon: Icon.CreditCard,
  access,
  go: (navigate) => void navigate({ to: '/billing', search: { tab } }),
}));

export const COMMAND_PAGES: CommandPageItem[] = [
  {
    id: 'email-accounts',
    label: 'Email Accounts',
    breadcrumb: 'Workspace › Email Accounts',
    route: '/email-accounts',
    icon: Icon.Mail,
    go: (navigate) => void navigate({ to: '/email-accounts' }),
  },
  {
    id: 'action-items',
    label: 'Action Items',
    breadcrumb: 'Account › Action Items',
    route: '/action-items',
    icon: Icon.AlertTriangle,
    go: (navigate) => void navigate({ to: '/action-items' }),
  },
  {
    id: 'templates',
    label: 'Templates',
    breadcrumb: 'Workspace › Templates',
    route: '/templates',
    icon: Icon.FileText,
    keywords: ['coming soon'],
    go: (navigate) => void navigate({ to: '/templates' }),
  },
  {
    id: 'notifications',
    label: 'Notifications',
    breadcrumb: 'Account › Notifications',
    route: '/notifications',
    icon: Icon.Bell,
    go: (navigate) => void navigate({ to: '/notifications' }),
  },
  {
    id: 'api-keys',
    label: 'API Keys',
    breadcrumb: 'Organization › API Keys',
    route: '/api-keys',
    icon: Icon.Lock,
    access: ACCESS.apiKeys,
    go: (navigate) => void navigate({ to: '/api-keys' }),
  },
  {
    id: 'partner-integration',
    label: 'Partner Integration',
    breadcrumb: 'Organization › Partner Integration',
    route: '/partner-integration',
    icon: Icon.Link,
    // Same gate as `Sidebar.tsx`'s `orgNavItems` filter.
    access: ACCESS.partner,
    go: (navigate) => void navigate({ to: '/partner-integration' }),
  },
  {
    id: 'audit-logs',
    label: 'Audit Logs',
    breadcrumb: 'Organization › Audit Logs',
    route: '/audit-logs',
    icon: Icon.Shield,
    // Same gate as `Sidebar.tsx`'s `orgNavItems` filter.
    access: ACCESS.audit,
    go: (navigate) => void navigate({ to: '/audit-logs' }),
  },
  ...BILLING_PAGES,
  ...SETTINGS_PAGES,
];

const COMMAND_PAGES_BY_ID: ReadonlyMap<string, CommandPageItem> = new Map(
  COMMAND_PAGES.map((page) => [page.id, page]),
);

export function findCommandPage(id: string): CommandPageItem | undefined {
  return COMMAND_PAGES_BY_ID.get(id);
}

/** Role-based visibility filter — mirrors `Sidebar.tsx`/`SettingsRail.tsx` (same map, `useRoleCan().can`). */
export function visibleCommandPages(
  pages: readonly CommandPageItem[],
  can: (access?: Access | null) => boolean,
): CommandPageItem[] {
  return pages.filter((page) => can(page.access));
}
